import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, map } from 'rxjs';
import {
  IMAGE_SRC,
  VALIDATE_ADDRESS_RESPONSES,
  VALIDATION_MESSAGE,
} from 'src/app/components/shared/shared-constants';
import { nonCanadianPostalValidator } from 'src/app/core/custom-validators/non-canadian-postal/non-canadian-postal.validator';
import { SpecialCharacters } from 'src/app/core/enums';
import { CurrentLocation, UserAdmin } from 'src/app/core/models/admin';
import {
  SaveScheduledPickupRequest,
  ValidateAddressRequest,
} from 'src/app/core/models/operations/operations-pickup.model';
import {
  Days,
  ScheduledPickup,
} from 'src/app/core/models/operations/operations-scheduled-pickup.model';
import { LocationService } from 'src/app/core/services/admin/location/location.service';
import { PickupAssignmentsService } from 'src/app/core/services/operations/pickup-assignments/pickup-assignments.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { OperationsUtils } from 'src/app/core/utils/operations/operations.utils';
import { ServiceSupportUtils } from 'src/app/core/utils/service-support/service-support.utils';
import { CONSTANTS, PICKUPS } from './scheduled-pickup.constants';
import {
  ScheduledPickupForm,
  ScheduledPickupInputData,
} from './scheduled-pickup.model';

@Component({
  selector: 'app-scheduled-pickup',
  templateUrl: './scheduled-pickup.component.html',
  styleUrls: ['./scheduled-pickup.component.scss'],
  standalone: false,
})
export class ScheduledPickupComponent implements OnInit {
  data = inject<ScheduledPickupInputData>(MAT_DIALOG_DATA);
  private readonly destroy = inject(DestroyRef);
  private readonly locationService = inject(LocationService);
  private readonly userService = inject(UserService);
  private dialogRef = inject(MatDialogRef<ScheduledPickupComponent>);
  private fb = inject(NonNullableFormBuilder);
  private serviceSupportUtils = inject(ServiceSupportUtils);
  private pickupAssignmentsService = inject(PickupAssignmentsService);
  private operationsUtils = inject(OperationsUtils);
  private commonUtils = inject(CommonUtils);

  scheduledPickupForm!: FormGroup<ScheduledPickupForm>;
  isIDO$ = new BehaviorSubject<boolean>(false);

  readonly CONSTS = {
    ...CONSTANTS,
    ...VALIDATION_MESSAGE,
    ...VALIDATE_ADDRESS_RESPONSES,
  };

  readonly CONTROLS = PICKUPS;
  readonly SRC = IMAGE_SRC;
  readonly REGEX_LATLONG = SpecialCharacters.LAT_LONG;
  readonly REGEX_ALPHA = SpecialCharacters.ALPHABET_ONLY;
  readonly CANADIAN_POSTAL = SpecialCharacters.CANADIAN_POSTAL;

  validated = false;
  validationCode = '';
  validatedLatNr = '';
  validatedLngNr = '';
  currentUser!: UserAdmin;
  userLocation!: CurrentLocation;
  location$ = new BehaviorSubject<CurrentLocation | null>(null);
  countryCode$ = new BehaviorSubject<string>('');

  minDate: Date = new Date();
  maxDate: Date = new Date(
    this.minDate.getFullYear(),
    this.minDate.getMonth(),
    this.minDate.getDate() + 14,
  );
  startMaxDate: Date = new Date(this.minDate.getFullYear() + 10, 11, 31);
  startMinDate: Date = new Date();

  endMaxDate: Date = new Date(2109, 11, 31);

  futureDate: Date = new Date(
    this.minDate.getFullYear(),
    this.minDate.getMonth(),
    this.minDate.getDate() + 60,
  );
  endDate: Date = new Date('12/31/2099');
  startDate: Date = new Date();

  scheduledMaxDate: Date = new Date(this.minDate.getFullYear() + 10, 11, 31);
  scheduledMaxEndDate: Date = new Date(2099, 11, 31);

  defaultStartTime = '';
  defaultEndTime = '';
  disabled = true;

  scheduledPickupData!: ScheduledPickup;

  ngOnInit() {
    this.userService
      .getCurrentUser$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        map((user) => {
          this.currentUser = user;
          this.scheduledPickupData = this.data.data?.response?.schedulePickup;
          this.initializeForm();
        }),
      )
      .subscribe();

    this.locationService
      .getCurrentLocation$()
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((profile) => {
        this.countryCode$.next(profile.location?.address?.isoCode ?? '');
        this.location$.next(profile);
      });

    this.data.routes = this.data.routes
      .filter(this.operationsUtils.getUniqueValues)
      .sort((a, b) => a.localeCompare(b));

    this.commonUtils.autoCapitalize(
      this.scheduledPickupForm,
      [
        this.CONTROLS.PICKUP_REQUEST_NUMBER,
        this.CONTROLS.PICKUP_POINT,
        this.CONTROLS.CONTACT_NUMBER,
      ],
      this.destroy,
    );
  }

  initializeForm() {
    const isValidatorOptional = this.commonUtils.isStateProvinceOptional(
      this.data.countryCode,
      this.currentUser.locationGroup,
      this.currentUser.location,
    );

    const data = this.scheduledPickupData;

    this.scheduledPickupForm = this.fb.group<ScheduledPickupForm>({
      ondemandPickUp: this.fb.control(data.ondemandPickUp),
      daysAssigned: this.fb.control(data.daysAssigned, Validators.required),
      pickupRequestNumber: this.fb.control({
        value: '',
        disabled: true,
      }),
      pickupPoint: this.fb.control(data.pickupPoint),
      route: this.fb.control(data.route),
      startDate: this.fb.control(
        data.startDate ? new Date(data.startDate) : null,
      ),
      endDate: this.fb.control(data.endDate ? new Date(data.endDate) : null),
      readyTime: this.fb.control(data.readyTime, Validators.minLength(5)),
      commitTime: this.fb.control(data.commitTime, Validators.minLength(5)),
      closingTime: this.fb.control(data.closingTime, Validators.minLength(5)),
      interruptDate: this.fb.control(
        data.interruptDate ? new Date(data.interruptDate) : null,
      ),
      restartDate: this.fb.control(
        data.restartDate ? new Date(data.restartDate) : null,
      ),
      shipperNumber: this.fb.control(
        data.customerInfo?.shipperNo,
        Validators.required,
      ),
      customerName: this.fb.control(
        data.customerInfo?.customerName,
        Validators.required,
      ),
      contactName: this.fb.control(data.customerInfo?.contactName),
      contactNumber: this.fb.control(data.customerInfo?.contactNumber),
      streetAddress: this.fb.control(data.customerInfo?.streetAddress),
      prefix: this.fb.control(data.customerInfo?.prefix ?? ''),
      streetName: this.fb.control(
        data.customerInfo?.streetName,
        Validators.required,
      ),
      suffix: this.fb.control(data.customerInfo?.suffix ?? ''),
      suite: this.fb.control(data.customerInfo?.suite ?? ''),
      floor: this.fb.control(data.customerInfo?.floor ?? ''),
      city: this.fb.control(data.customerInfo?.cityTown),
      province: this.fb.control(
        data.customerInfo?.province ?? '',
        isValidatorOptional ? Validators.nullValidator : Validators.required,
      ),
      postalCode: this.fb.control(
        data.customerInfo?.postalCode,
        Validators.compose([
          Validators.required,
          nonCanadianPostalValidator(this.data.countryCode),
        ]),
      ),
      latitude: this.fb.control(data.customerInfo?.latitude),
      longitude: this.fb.control(data.customerInfo?.longitude),
      sscDescription: this.fb.control({
        value: data.sscDescription?.replaceAll(',', '\n') ?? '',
        disabled: true,
      }),
      additionalInfo: this.fb.control(data.additionalInfo ?? ''),
    });
  }

  dataChanged(): void {
    this.scheduledPickupForm.markAsDirty();
  }

  onSubmit() {
    const data = this.scheduledPickupForm.getRawValue();

    const request: SaveScheduledPickupRequest = {
      schedulePickup: {
        id: this.data.id,
        status: this.data.status,
        IDOsuggestion: this.data.IDOsuggestion,
        packageType: this.data.packageType,
        ondemandPickUp: data.ondemandPickUp,
        daysAssigned: data.daysAssigned,
        pickupRequest: data.pickupRequestNumber,
        pickupPoint: data.pickupPoint,
        route: data.route,
        startDate: this.serviceSupportUtils.getMonthFirstFormattedDate(
          data.startDate,
        ),
        endDate: this.serviceSupportUtils.getMonthFirstFormattedDate(
          data.endDate,
        ),
        interruptDate: this.serviceSupportUtils.getMonthFirstFormattedDate(
          data.interruptDate,
        ),
        restartDate: this.serviceSupportUtils.getMonthFirstFormattedDate(
          data.restartDate,
        ),
        readyTime: data.readyTime,
        commitTime: data.commitTime,
        closingTime: data.closingTime,
        customerInfo: {
          shipperNo: data.shipperNumber,
          customerName: data.customerName,
          contactName: data.contactName,
          contactNumber: data.contactNumber,
          streetAddress: data.streetAddress,
          prefix: data.prefix,
          streetName: data.streetName,
          suffix: data.suffix,
          suite: data.suite,
          floor: data.floor,
          cityTown: data.city,
          province: data.province,
          postalCode: data.postalCode,
          latitude: data.latitude,
          longitude: data.longitude,
        },
        additionalInfo: data.additionalInfo,
        SSCDescription: data.sscDescription,
      },
    };

    this.dialogRef.close(request);
  }

  clickExit() {
    this.commonUtils.openExitModal(this.scheduledPickupForm.dirty);
  }

  validate() {
    const validateAddressRequest: ValidateAddressRequest = {
      addressValidation: {
        address: {
          consignee: this.scheduledPickupForm.get(this.CONTROLS.CUSTOMER_NAME)
            ?.value,
          city: this.scheduledPickupForm.get(this.CONTROLS.CITY)?.value,
          state: this.scheduledPickupForm.get(this.CONTROLS.PROVINCE)?.value,
          postalCode: this.scheduledPickupForm.get(this.CONTROLS.POSTAL_CODE)
            ?.value,
          country: this.data.countryCode,
          floor: this.scheduledPickupForm.get(this.CONTROLS.FLOOR)?.value,
          suite: this.scheduledPickupForm.get(this.CONTROLS.SUITE)?.value,
          streetParsed: {
            number: this.scheduledPickupForm.get(this.CONTROLS.STREET_ADDRESS)
              ?.value,
            name: this.scheduledPickupForm.get(this.CONTROLS.STREET_NAME)
              ?.value,
            prefix: this.scheduledPickupForm.get(this.CONTROLS.PREFIX)?.value,
            suffix: this.scheduledPickupForm.get(this.CONTROLS.SUFFIX)?.value,
            type: '',
          },
        },
        userId: this.currentUser.loginId,
        orderNumber: this.data.orderNumber,
      },
    };

    this.pickupAssignmentsService
      .validateAddress(validateAddressRequest, this.currentUser.location)
      .pipe(
        takeUntilDestroyed(this.destroy),
        map((data) => {
          this.validated = true;
          this.validationCode = data.status;

          if (data.status === '0' && data.address) {
            this.validatedLatNr = data.address?.latNr;
            this.validatedLngNr = data.address?.lngNr;
            this.scheduledPickupForm
              .get(PICKUPS.LATITUDE)
              ?.setValue(this.validatedLatNr);

            this.scheduledPickupForm
              .get(PICKUPS.LONGITUDE)
              ?.setValue(this.validatedLngNr);
          }
        }),
      )
      .subscribe();
  }

  daysAssignedUpdated(event: Days) {
    this.scheduledPickupForm.get(PICKUPS.DAYS_ASSIGNED)?.setValue(event);
    this.scheduledPickupForm.markAsDirty();
  }
}
